'use client'

import Image from 'next/image'
import Link from 'next/link'
import { useTranslations } from 'next-intl'
import {
  MapPin,
  Building2,
  Globe,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import type { TenantBranch } from '@/services/booking-context'

interface BranchCardProps {
  branch: TenantBranch
}

export function BranchCard({ branch }: BranchCardProps) {
  const t = useTranslations('booking.branches')

  return (
    <div className="group bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden shadow-sm hover:shadow-md transition-shadow">
      {/* Photo header */}
      <div className="relative aspect-[16/9] bg-gray-100 dark:bg-gray-700 overflow-hidden">
        {branch.photoUrl ? (
          <Image
            src={branch.photoUrl}
            alt={branch.name_en}
            fill
            className="object-cover group-hover:scale-105 transition-transform duration-300"
            sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            <Building2 className="h-12 w-12 text-gray-300 dark:text-gray-600" />
          </div>
        )}

        {/* Booking badge */}
        {branch.isBookingEnabled && (
          <div className="absolute top-3 right-3">
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-green-500/90 text-white text-[10px] font-semibold backdrop-blur-sm">
              <Globe className="h-3 w-3" />
              {t('bookOnline')}
            </span>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-4 space-y-3">
        {/* Name */}
        <div>
          <h3 className="font-semibold text-gray-900 dark:text-gray-100 text-base leading-tight">
            {branch.name_en}
          </h3>
          {branch.name_ar && (
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5" dir="rtl">
              {branch.name_ar}
            </p>
          )}
        </div>

        {/* Location */}
        {(branch.city || branch.address) && (
          <div className="flex items-start gap-2 text-sm text-gray-600 dark:text-gray-400">
            <MapPin className="h-4 w-4 shrink-0 mt-0.5 text-gray-400" />
            <div>
              {branch.city && <span className="font-medium">{branch.city}</span>}
              {branch.address && branch.city && <span> &middot; </span>}
              {branch.address && <span>{branch.address}</span>}
            </div>
          </div>
        )}

        {/* CTA */}
        <div className="pt-1">
          {branch.code ? (
            <Link href={`/booking/property/${branch.code}`} className="block">
              <Button className="w-full" size="sm">
                {t('viewAndBook')}
              </Button>
            </Link>
          ) : null}
        </div>
      </div>
    </div>
  )
}
